import i18n from 'i18next';

/**
 * 管理員密碼政策。
 *
 * ⚠️ 這裡的規則是硬編碼，與 Firebase console 的 Authentication → Settings →
 * Password policy 設定之間「沒有任何自動同步機制」。任何一邊變更都必須同步另一邊，
 * 否則會產生「前端通過但 Firebase 拒絕」的死路。設定為 per project，dev / stage /
 * production 三個 Firebase project 各自獨立，不會互相繼承。
 *
 * 之所以不用 SDK 的 `validatePassword(auth, password)` 向後端動態索取政策：該 API 自
 * firebase@10.2.0 起才提供，本專案目前為 firebase@9.23.0。升 major version 會波及
 * auth / firestore / storage 的 import 路徑與行為，不在本次改動範圍內。
 */

export const PASSWORD_MIN_LENGTH = 12;

/**
 * Google Cloud Identity Platform 認定的特殊字元字集，採「不含反引號」的保守版本。
 *
 * 兩份官方文件對此有出入：GCIP 文件的清單結尾包含反引號 `` ` ``，Firebase 文件的則沒有。
 * 在無法判定何者為準的情況下一律往嚴格的一邊倒 —— 前端比後端嚴格，最壞情況是使用者多換
 * 一個符號；前端比後端寬鬆則會產生前端通過、後端拒絕的死路。
 * 這是刻意的取捨，不是抄漏，請勿逕自補上反引號。
 *
 * 另請注意此字集「不包含」 + = - 與空白。連字號是使用者相當常用的符號。
 */
export const PASSWORD_SPECIAL_CHARS = `^$*.[]{}()?"!@#%&/\\,><':;|_~`;

// Firebase SDK 判定字元類別的方式是逐字元比較 ASCII 大小，而非 Unicode-aware 的正規表達式。
// 前端必須採用相同判定方式，否則會與 Firebase 的結果分歧：中文字與全形數字三種類別皆不計入，
// 僅對長度有貢獻；長度以 JavaScript 的 UTF-16 code unit 計算，emoji 等代理對會被計為 2 個字元。
const isAsciiLowercase = char => char >= 'a' && char <= 'z';
const isAsciiDigit = char => char >= '0' && char <= '9';
const isSpecialChar = char => PASSWORD_SPECIAL_CHARS.includes(char);

/**
 * 驗證密碼是否符合政策。純函式，不依賴 React 或 Firebase。
 *
 * 不論缺少哪一項條件都回傳同一則訊息，讓使用者一次看到全部門檻，不用改一條被擋一次。
 *
 * @param {string} password
 * @returns {{ valid: boolean, msg: string }}
 */
export function validatePasswordPolicy(password) {
    const chars = Array.from(password);
    const isValid =
        password.length >= PASSWORD_MIN_LENGTH &&
        chars.some(isAsciiLowercase) &&
        chars.some(isAsciiDigit) &&
        chars.some(isSpecialChar);

    return isValid ? { valid: true, msg: '' } : { valid: false, msg: getPasswordPolicyErrMsg() };
}

// 沿用既有的 key，只改寫其三個語系的值。key 名稱在新文案下語意上不夠精確（涵蓋的是複雜度
// 而不只是長度），但更名需同時變更程式碼、三個 locale 檔並確認 OneSky 不留下孤兒 key，
// 收益與成本不成比例。
const PASSWORD_POLICY_ERR_KEY = 'label.error.word_limit.pw'; // pragma: allowlist secret

export const getPasswordPolicyErrMsg = () => i18n.t(PASSWORD_POLICY_ERR_KEY);
